// Dialog para ver, subir y eliminar adjuntos de una solicitud de licencia.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
    List, ListItem, ListItemText, IconButton, CircularProgress, TextField, MenuItem, Chip,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { nodeApiPath } from '../utils/paths';
import { triggerSessionExpired } from '../api/endpoints';

const BR = '4px';

async function jf(path, options = {}) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) { triggerSessionExpired(); throw new Error('No hay token'); }
    const res = await fetch(nodeApiPath(path), {
        ...options,
        headers: {
            'Accept': 'application/json',
            ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
            'Authorization': `Bearer ${token}`,
            ...(options.headers || {}),
        },
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

const tiposAdjunto = [
    { value: 'DOC_USUARIO', label: 'Documento del usuario' },
    { value: 'JUNTA_MEDICA', label: 'Junta médica' },
    { value: 'DICTAMEN', label: 'Dictamen jurídico' },
    { value: 'DISPOSICION', label: 'Disposición firmada' },
];

// rolUsuario: 'EMPLEADO' | 'RRHH' | 'DIR_ADMIN' | 'JURIDICO' | 'AUTORIDAD' — determina qué tipos puede subir
export default function AdjuntosSolicitud({ open, onClose, idSolicitud, solicitud, puedeSubir = true, puedeEliminar = true, rolUsuario = 'EMPLEADO', notify }) {
    // Tipos permitidos según rol
    const tiposPermitidos = {
        EMPLEADO: ['DOC_USUARIO', 'JUNTA_MEDICA'],
        RRHH: ['DOC_USUARIO', 'JUNTA_MEDICA', 'DISPOSICION'],
        DIR_ADMIN: [],
        JURIDICO: ['DICTAMEN'],
        AUTORIDAD: ['DISPOSICION'],
    }[rolUsuario] || ['DOC_USUARIO'];

    // Bloquear subida si la solicitud está cerrada o ya avanzó del paso actual del rol
    const estaCerrada = solicitud?.Estado && (
        solicitud.Estado === 'FINALIZADA' || solicitud.Estado === 'APROBADA' ||
        solicitud.Estado.startsWith('RECHAZADA')
    );

    // Solo se puede subir/eliminar si la solicitud está en un estado que corresponde al rol actual
    const estadosPorRol = {
        EMPLEADO: ['PENDIENTE_DIRECTOR', 'PENDIENTE_RRHH'],
        RRHH: ['PENDIENTE_RRHH', 'PENDIENTE_RRHH_DICTAMEN', 'EN_LOTE'],
        DIR_ADMIN: ['PENDIENTE_DIR_ADMIN'],
        JURIDICO: ['PENDIENTE_JURIDICO'],
        AUTORIDAD: ['PENDIENTE_AUTORIDAD'],
    };
    const estadosPermitidos = estadosPorRol[rolUsuario] || [];
    const esMiTurno = solicitud?.Estado && estadosPermitidos.includes(solicitud.Estado);
    const puedeSubirReal = puedeSubir && !estaCerrada && tiposPermitidos.length > 0 && esMiTurno;
    const puedeEliminarReal = puedeEliminar && !estaCerrada && esMiTurno;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [tipo, setTipo] = useState('DOC_USUARIO');
    const fileRef = useRef(null);

    const cargar = useCallback(async () => {
        if (!idSolicitud) return;
        setLoading(true);
        try {
            const r = await jf(`personal/solicitudes/${idSolicitud}/adjuntos`);
            setItems(r.ok ? (r.items || []) : []);
        } catch { setItems([]); }
        setLoading(false);
    }, [idSolicitud]);

    useEffect(() => { if (open) cargar(); }, [open, cargar]);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 20 * 1024 * 1024) { notify?.('Máximo 20MB', 'warning'); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('tipo', tipo);
            const token = localStorage.getItem('SIGIF_JWT_TOKEN');
            const res = await fetch(nodeApiPath(`personal/solicitudes/${idSolicitud}/adjuntos`), {
                method: 'POST', body: fd, headers: { Authorization: `Bearer ${token}` },
            });
            const r = await res.json();
            if (r.ok) { notify?.('Adjunto subido', 'success'); cargar(); }
            else notify?.(r.mensaje || 'Error', 'error');
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = '';
    };

    const descargar = (item) => {
        const token = localStorage.getItem('SIGIF_JWT_TOKEN');
        const url = nodeApiPath(`personal/adjuntos/${item.Id}`);
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.blob())
            .then(blob => {
                const u = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = u; a.download = item.Nombre_Original; document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(u), 1000);
            }).catch(() => notify?.('Error descargando', 'error'));
    };

    const previsualizar = (item) => {
        const token = localStorage.getItem('SIGIF_JWT_TOKEN');
        const url = nodeApiPath(`personal/adjuntos/${item.Id}`);
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.blob())
            .then(blob => {
                const u = URL.createObjectURL(blob);
                const w = window.open(u, '_blank');
                if (!w) { notify?.('El navegador bloqueó la ventana emergente', 'warning'); }
            }).catch(() => notify?.('Error abriendo', 'error'));
    };

    const esPrevisualizable = (mime) => mime && (mime.startsWith('image/') || mime === 'application/pdf');

    const eliminar = async (item) => {
        try {
            const r = await jf(`personal/adjuntos/${item.Id}`, { method: 'DELETE' });
            if (r.ok) { notify?.('Eliminado', 'info'); cargar(); }
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
    };

    const tipoLabel = (t) => tiposAdjunto.find(x => x.value === t)?.label || t;
    const kb = (b) => b ? (b / 1024 < 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`) : '';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: BR } }}>
            <DialogTitle sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachFileIcon />
                Adjuntos {solicitud?.Tipo ? `— ${solicitud.Tipo}` : ''}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                {puedeSubirReal && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                        <TextField select size="small" value={tipo} onChange={e => setTipo(e.target.value)} sx={{ minWidth: 200 }}>
                            {tiposAdjunto.filter(t => tiposPermitidos.includes(t.value)).map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                        </TextField>
                        <Button variant="contained" startIcon={<AttachFileIcon />} component="label"
                            disabled={uploading}
                            sx={{ bgcolor: '#1565c0', textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: '#0d47a1' } }}>
                            {uploading ? 'Subiendo...' : 'Subir archivo'}
                            <input ref={fileRef} type="file" hidden onChange={handleFile} />
                        </Button>
                    </Box>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
                ) : items.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#999', py: 2, textAlign: 'center' }}>Sin adjuntos</Typography>
                ) : (
                    <List>
                        {items.map(it => (
                            <ListItem key={it.Id} sx={{ border: '1px solid #e0e0e0', borderRadius: BR, mb: 1 }}
                                secondaryAction={
                                    <Box>
                                        {esPrevisualizable(it.Mime_Type) && (
                                            <Tooltip title="Ver sin descargar"><IconButton size="small" onClick={() => previsualizar(it)} sx={{ color: '#2e7d32' }}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                                        )}
                                        <Tooltip title="Descargar"><IconButton size="small" onClick={() => descargar(it)} sx={{ color: '#1565c0' }}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                                        {puedeEliminarReal && <Tooltip title="Eliminar"><IconButton size="small" onClick={() => eliminar(it)} sx={{ color: '#c62828' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                                    </Box>
                                }>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{it.Nombre_Original}</Typography>
                                            <Chip label={tipoLabel(it.Tipo)} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                                        </Box>
                                    }
                                    secondary={`${kb(it.Tamano_Bytes)} · ${it.Subido_Por || ''} · ${new Date(it.Fecha).toLocaleString('es-AR')}`}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} startIcon={<CloseIcon />} sx={{ textTransform: 'none' }}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
}
